/**
 * Credential Level — presentation helpers (Frontend)
 *
 * The credential level (L3 / L4 / L5) is a PRODUCTION-AUTHORITATIVE FACT carried
 * per-programme on the record — never a blanket marketing claim. These helpers only
 * FORMAT that fact for display; they never assert or default a level for a real
 * programme (the backend always supplies one). `LEVEL_RANGE_LABEL` is the honest
 * portfolio-wide phrasing for generic marketing surfaces where no single programme
 * is in scope.
 */

export const CREDENTIAL_LEVELS = ['L3', 'L4', 'L5'];

// Framework-equivalence FACTS mirror backend config.specialist_taxonomy.CREDENTIAL_LEVEL_META.
// Always expressed as "equivalent" — CPDForge credentials are not placed on a
// regulated framework, they are aligned/equivalent to it (Qualification Principle).
export const CREDENTIAL_LEVEL_META = {
  L3: { code: 'L3', label: 'Level 3', num: '3', rqf: 'RQF Level 3 equivalent', scqf: 'SCQF Level 6 equivalent', eqf: 'EQF Level 4 equivalent' },
  L4: { code: 'L4', label: 'Level 4', num: '4', rqf: 'RQF Level 4 equivalent', scqf: 'SCQF Level 7 equivalent', eqf: 'EQF Level 5 equivalent' },
  L5: { code: 'L5', label: 'Level 5', num: '5', rqf: 'RQF Level 5 equivalent', scqf: 'SCQF Level 8 equivalent', eqf: 'EQF Level 5 equivalent' },
};

/** Portfolio-wide range phrasing for generic marketing (no single programme in scope). */
export const LEVEL_RANGE_LABEL = 'Level 3–5';

const _norm = (code) => {
  const v = String(code || '').trim().toUpperCase();
  return CREDENTIAL_LEVELS.includes(v) ? v : 'L3';
};

/** "Level 3" | "Level 4" | "Level 5" for a programme's governed credential_level. */
export const levelLabel = (code) => CREDENTIAL_LEVEL_META[_norm(code)].label;

/** Full framework-equivalence line, e.g. "RQF Level 5 equivalent (SCQF Level 8 / EQF Level 5 equivalent)". */
export const levelEquivalence = (code) => {
  const m = CREDENTIAL_LEVEL_META[_norm(code)];
  return `${m.rqf} (${m.scqf} / ${m.eqf})`;
};

/** Level meta object for a programme's governed credential_level. */
export const levelMeta = (code) => CREDENTIAL_LEVEL_META[_norm(code)];
